import { useCallback, useState } from 'react';
import { getLogger } from '@packages/core/logging';
import { docsService } from '@web/services/singletons';
import { fetchCdnDocumentUrlByDocId, openDocumentTab } from '@web/vin-sessions/utils/open-document-tab';
import { ContentFilePreview, downloadFromUrl } from '../content-file.utils';

const logger = getLogger('vin-sessions');

export type OpenContentFileError = 'blocked' | 'failed';

interface Options {
    publicationFormId?: string;
    preview: ContentFilePreview | null;
    token: string;
    onError: (error: OpenContentFileError) => void;
}

export const useOpenContentFile = ({ publicationFormId, preview, token, onError }: Options) => {
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    const open = useCallback(async () => {
        if (!publicationFormId || !preview) {
            return;
        }
        if (preview === 'manual') {
            await openDocumentTab(
                () => fetchCdnDocumentUrlByDocId(publicationFormId, token),
                () => onError('blocked')
            );
            return;
        }
        try {
            const response = await docsService.getDocumentUrl({
                queryParameters: { docId: publicationFormId },
                ignoreCache: true,
            });
            const url = response.success && typeof response.data === 'string' ? response.data : null;
            if (!url) {
                onError('failed');
                return;
            }
            if (preview === 'pdf') {
                setPdfUrl(url);
            } else {
                downloadFromUrl(url);
            }
        } catch (error) {
            logger.error(`Failed to open the file of the publication [${publicationFormId}]`, { error });
            onError('failed');
        }
    }, [publicationFormId, preview, token, onError]);

    const closePdf = useCallback(() => setPdfUrl(null), []);

    return { open, pdfUrl, closePdf };
};
